import type { Middleware } from 'openapi-fetch'

import { queryClient } from './queryClient'

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

/** Writes that only read, save a preference, or manage the cache themselves (the company switcher). */
const NO_REFRESH_PATHS: RegExp[] = [
  /\/route-estimate$/,
  /\/e-tickets\/quote-options$/,
  /\/auth\/me\/locale$/,
  /\/auth\/me\/selected-company$/,
  /\/user-preferences\/dashboard-layout\//,
]

/** Set `meta: { refreshAfterWrite: false }` on a query whose own fetch is a costly write, such as a supplier quote. */
export const SKIP_REFRESH_AFTER_WRITE = { refreshAfterWrite: false } as const

function isRefreshingWrite(method: string, url: string): boolean {
  if (!WRITE_METHODS.has(method.toUpperCase())) return false

  const { pathname } = new URL(url, window.location.origin)
  return !NO_REFRESH_PATHS.some((path) => path.test(pathname))
}

export function refreshesAfterWrite(method: string, url: string, status: number): boolean {
  if (status < 200 || status >= 300) return false

  return isRefreshingWrite(method, url)
}

let writesInFlight = 0
let isRefreshDue = false

function refreshOnceWritesSettle(): void {
  // A timer, not a microtask: the next step of a multi-request save starts before it fires, so one refresh follows the last step.
  setTimeout((): void => {
    if (writesInFlight > 0 || !isRefreshDue) return

    isRefreshDue = false
    // cancelRefetch false: a refetch a mutation already started is reused instead of being aborted and sent again.
    void queryClient.invalidateQueries(
      { predicate: (query): boolean => query.meta?.refreshAfterWrite !== false },
      { cancelRefetch: false },
    )
  }, 0)
}

function settleWrite(request: Request, status: number | null): void {
  if (!isRefreshingWrite(request.method, request.url)) return

  writesInFlight = Math.max(0, writesInFlight - 1)
  if (status !== null && status >= 200 && status < 300) isRefreshDue = true
  refreshOnceWritesSettle()
}

/**
 * After a successful write, every cached query is marked stale: what is on screen refetches, and a modal
 * opened again loads fresh data instead of the process that was just submitted. Like a page refresh, without reloading.
 */
export const refreshAfterWriteMiddleware: Middleware = {
  onRequest({ request }): undefined {
    if (isRefreshingWrite(request.method, request.url)) writesInFlight += 1

    return undefined
  },
  onResponse({ request, response }): Response {
    settleWrite(request, response.status)

    return response
  },
  onError({ request }): undefined {
    settleWrite(request, null)

    return undefined
  },
}
